import {
  Button,
  ButtonVariants,
} from "../../../../../../components/core/Button/Button";
import { EmptyState } from "../../../../../../components/EmptyState/EmptyState";
import {
  DELETE_EXPERIENCE_CONFIRM,
  DELETE_EXPERIENCE_LABEL,
  EMPTY_PAST_EXPERIENCES_MESSAGE,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
  NAME_LABEL,
  EXPERIENCES_LABEL,
  EXPERIENCE_LABEL,
  EXPERIENCE_DELETE_ERROR_MESSAGE,
  DATA_DELETED_SUCCESS_LABEL,
} from "../../../../../../constants/Dictionary";
import { StyledPastExperiences } from "./PastExperiences.styled";
import emptyPastExperiencesImage from "../../../../../../images/past_experiences_empty.svg";
import { ReactComponent as AddIcon } from "../../../../../../icons/add.svg";
import {
  CollaboratorCmSheet,
  CollaboratorDetails,
  CollaboratorHistoryProfessionalExperience,
} from "../../../../../../types/api/collaborators.type";
import { StyledExpandTable } from "../../ProfessionalHistory.styled";
import {
  Table,
  TableThemeEnum,
} from "../../../../../../components/core/Table/Table";
import { useAppSelector } from "../../../../../../hooks/store.hook";
import React, { useEffect, useState } from "react";
import {
  COLLABORATORS_PAGE_SIZE,
  CustomerErrorCodesEnum,
  StatusCodes,
} from "../../../../../../constants/AppConstants";
import { collaboratorHistoryExperienceColumns } from "../../../../../../constants/TableColumns";
import { useDebounce } from "use-debounce";
import { DoubleStructure } from "../../../../../../components/core/DoubleStructure/DoubleStructure";
import {
  AlignmentEnum,
  FontWeightEnum,
} from "../../../../../../types/AppTypes";
import { BodyTyp } from "../../../../../../components/Typography/Typography";
import SearchInput from "../../../../../../components/core/SearchInput/SearchInput";
import { Loader } from "../../../../../../components/core/Loader/Loader";
import LoadingThumbnail from "../../../../../../components/LoadingThumbnail/LoadingThumbnail";
import { PastExperienceModal } from "./PastExperiencesModal/PastExperienceModal";
import { PopupConfirm } from "../../../../../../components/PopupConfirm/PopupConfirm";
import { useNotification } from "../../../../../../hooks/notification.hook";
import { AxiosError } from "axios";
import { SingleErrorResponse } from "../../../../../../types/api/generic.type";
import { canEditCollaboratorHistory } from "../../../../../../utils/canEditCollaboratorHistory.utils";
import {
  useDeleteHistoryExperience,
  useHistoryExperience,
} from "../../../../../../hooks/professionalPortrait.hook";

type PastExperiencesProps = {
  collaborator: CollaboratorDetails;
  showAddModal: boolean;
  setShowAddModal: () => void;
  onStatusRefresh: () => void;
  onDataChange?: (hasData: boolean) => void;
  isMyProfile?: boolean;
  currentCmSheet?: CollaboratorCmSheet;
};

const PastExperiences = ({
  collaborator,
  isMyProfile,
  showAddModal,
  setShowAddModal,
  onDataChange,
  onStatusRefresh,
  currentCmSheet,
}: PastExperiencesProps) => {
  const { notification } = useNotification();

  const { profile } = useAppSelector((state) => state.user);
  const profileId = collaborator.id;

  const [pageNumber, setPageNumber] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [searchDebouncedValue] = useDebounce(searchValue, 500);
  const [hasData, setHasData] = useState<boolean | null>(null);
  const [selectedExperience, setSelectedExperience] =
    useState<CollaboratorHistoryProfessionalExperience | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data: historyExperiencesData, isFetching: experienceLoading } =
    useHistoryExperience({ profileId, pageNumber, searchDebouncedValue });
  const {
    mutateAsync: deleteHistoryExperience,
    isPending: isDeletingHistoryExperience,
  } = useDeleteHistoryExperience(profileId);

  const restrictCmActions = currentCmSheet?.careerManagerId === profile.id;

  const canEditHistory = canEditCollaboratorHistory(
    profile,
    collaborator,
    currentCmSheet,
    isMyProfile,
    restrictCmActions,
  );

  useEffect(() => {
    const updateHasData = () => {
      if (!experienceLoading && historyExperiencesData && hasData === null) {
        const result = historyExperiencesData.pagination.total > 0;
        setHasData(result);
        onDataChange?.(result);
      }
    };

    hasData ? null : updateHasData();
  }, [
    historyExperiencesData,
    onDataChange,
    hasData,
    experienceLoading,
    canEditHistory,
  ]);

  const rowKey = (record: CollaboratorHistoryProfessionalExperience) => {
    return record.id.toString();
  };

  const onEdit = (record: CollaboratorHistoryProfessionalExperience) => {
    setSelectedExperience(record);
    setShowAddModal();
  };

  const onDelete = (record: CollaboratorHistoryProfessionalExperience) => {
    setSelectedExperience(record);
    setShowDeleteModal(true);
  };

  const hideAddModal = () => {
    setSelectedExperience(null);
    setShowAddModal();
  };

  const handleSave = async (currentHasData: boolean | null) => {
    hideAddModal();
    if (currentHasData === false) {
      setHasData(null);
    }
    onStatusRefresh();
  };

  const handleDeletePastExperience = async () => {
    if (!selectedExperience) {
      return;
    }

    try {
      await deleteHistoryExperience(selectedExperience.id);
      onStatusRefresh();
      notification.success(DATA_DELETED_SUCCESS_LABEL, undefined, "toast");
    } catch (error) {
      console.error(error);
      const errorObj = error as AxiosError<SingleErrorResponse>;
      if (
        errorObj.response?.status === StatusCodes.CUSTOM_ERROR &&
        errorObj.response.data.code ===
          CustomerErrorCodesEnum.RELATED_PROJECTS_FOUND
      ) {
        notification.error(
          ERROR_LABEL,
          `${selectedExperience.companyName} ${EXPERIENCE_DELETE_ERROR_MESSAGE}`,
        );
      } else {
        notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
      }
    } finally {
      hideDeletePopup();
    }
  };

  const hideDeletePopup = () => {
    setSelectedExperience(null);
    setShowDeleteModal(false);
  };

  useEffect(() => {
    setPageNumber(0);
  }, [searchDebouncedValue]);

  return (
    <>
      <StyledPastExperiences>
        {experienceLoading && hasData === null ? (
          <Loader loading={true}>
            <LoadingThumbnail marginTop={316} />
          </Loader>
        ) : (
          <>
            {!hasData ? (
              <div className="empty-state">
                <EmptyState
                  imageSrc={emptyPastExperiencesImage}
                  label={EMPTY_PAST_EXPERIENCES_MESSAGE}
                >
                  {canEditHistory && (
                    <Button
                      label={EXPERIENCE_LABEL}
                      variant={ButtonVariants.SECONDARY}
                      icon={<AddIcon />}
                      onClick={() => setShowAddModal()}
                    />
                  )}
                </EmptyState>
              </div>
            ) : (
              <>
                <DoubleStructure verticalAlignment={AlignmentEnum.CENTER}>
                  <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                    {`${historyExperiencesData?.pagination.total} ${historyExperiencesData?.data.length == 1 ? EXPERIENCE_LABEL : EXPERIENCES_LABEL}`}
                  </BodyTyp>
                  <div className="filters">
                    <SearchInput
                      onChange={setSearchValue}
                      label={NAME_LABEL}
                      name={"search"}
                      value={searchValue}
                    />
                  </div>
                </DoubleStructure>
                <StyledExpandTable>
                  <Table<CollaboratorHistoryProfessionalExperience>
                    theme={TableThemeEnum.DARK}
                    loading={experienceLoading}
                    rowKey={rowKey}
                    headers={collaboratorHistoryExperienceColumns}
                    scrollY={"calc(100dvh - 500px)"}
                    onEdit={canEditHistory ? onEdit : undefined}
                    onDelete={canEditHistory ? onDelete : undefined}
                    rowHasDelete={(row) =>
                      !(row.isAbstract && row.endDate === null)
                    }
                    rowHasEdit={(row) =>
                      !(row.isAbstract && row.endDate === null)
                    }
                    data={historyExperiencesData?.data || []}
                    pagination={{
                      current: pageNumber + 1,
                      onChange: (pageNumber) => setPageNumber(pageNumber - 1),
                      total: historyExperiencesData?.pagination.total ?? 0,
                      pageSize: COLLABORATORS_PAGE_SIZE,
                    }}
                    emptyText={EMPTY_PAST_EXPERIENCES_MESSAGE}
                  />
                </StyledExpandTable>
              </>
            )}
          </>
        )}
      </StyledPastExperiences>
      {showAddModal && (
        <PastExperienceModal
          record={selectedExperience}
          isOpen={showAddModal}
          onSave={() => handleSave(hasData)}
          onClose={() => hideAddModal()}
          collaborator={collaborator}
          {...(selectedExperience && {
            record: selectedExperience,
          })}
        />
      )}
      <PopupConfirm
        isOpen={showDeleteModal}
        label={DELETE_EXPERIENCE_LABEL}
        popupText={`${DELETE_EXPERIENCE_CONFIRM} ${selectedExperience?.companyName}?`}
        onConfirm={() => {
          handleDeletePastExperience();
        }}
        onCancel={() => {
          hideDeletePopup();
        }}
        isLoading={isDeletingHistoryExperience}
      />
    </>
  );
};

export default PastExperiences;
